/**
 * CompanyDetailPage — company detail (yritysrekisteri, #763/#764)
 *
 * Shows basic info, billing details, contact persons (person-company links),
 * the vehicles the company is an active party to and the work orders it has
 * ordered (#2519). Only the company's own links count: a car that belongs to a
 * contact person privately is on that person's card, not here.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  useCompany,
  useDeleteCompany,
} from '@/hooks/useCompanies';
import { AttachmentsCard } from '@/components/attachments/AttachmentsCard';
import { CustomerEmailDeliverySection } from '@/components/email/CustomerEmailDeliverySection';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { readCompanyConsolidationFlags } from '@/components/companies/CompanyFormFields';
import { PartyVehiclesSection } from '@/components/parties/PartyVehiclesSection';
import { PartyWorkOrdersSection } from '@/components/parties/PartyWorkOrdersSection';
import { PersonCompanyLinksSection } from '@/components/persons/PersonCompanyLinksSection';
import {
  ConfirmDialog,
  DetailField,
  DetailPageHeader,
  DetailSection,
  PermissionGuard,
} from '@/components/common';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';

/** #3919: the delete was refused because a courtesy loan names the company as its borrower. */
function isCourtesyLoanBorrower(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    typeof error.data === 'object' &&
    error.data !== null &&
    (error.data as { error?: unknown }).error === 'COMPANY_HAS_COURTESY_LOANS'
  );
}

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('companies');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();

  const { data: company, isLoading } = useCompany(id);
  const deleteMutation = useDeleteCompany();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return <Typography color="text.secondary">{t('empty')}</Typography>;
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(company.id);
      notify.success(t('deleted'), '');
      navigate('/parties?tab=companies');
    } catch (error) {
      // #3919: a company a courtesy loan names as its borrower is kept (409).
      if (isCourtesyLoanBorrower(error)) {
        setConfirmDelete(false);
        notify.error(t('deleteBlockedByLoan'), t('deleteBlockedByLoanDesc'));
        return;
      }
      notify.error(tc('error'), '');
    }
  };

  const consolidation = readCompanyConsolidationFlags(company);
  const yesNo = (on: boolean) => (on ? t('billing.yes') : t('billing.no'));

  const addr = [company.streetAddress, [company.postalCode, company.city].filter(Boolean).join(' ')]
    .filter((part) => part && part.length > 0)
    .join(', ');
  const billingAddr = [
    company.billingStreetAddress,
    [company.billingPostalCode, company.billingCity].filter(Boolean).join(' '),
  ]
    .filter((part) => part && part.length > 0)
    .join(', ');

  return (
    <Box>
      <DetailPageHeader
        title={company.name}
        onBack={() => navigate('/parties?tab=companies')}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setConfirmDelete(true)}
      />

      <Stack spacing={3}>
        <DetailSection title={t('sections.basicInfo')}>
          <Stack spacing={2}>
            <DetailField label={t('fields.businessId')} value={company.businessId} />
            <DetailField label={t('fields.phone')} value={company.phone} />
            <DetailField label={t('fields.email')} value={company.email} />
            <DetailField label={t('fields.website')} value={company.website} />
            <DetailField label={t('sections.address')} value={addr || null} />
            <DetailField label={t('fields.notes')} value={company.notes} />
          </Stack>
        </DetailSection>

        <DetailSection title={t('billing.title')}>
          <Stack spacing={2}>
            <DetailField label={t('billing.billingStreetAddress')} value={billingAddr || null} />
            <DetailField label={t('billing.invoiceEmail')} value={company.invoiceEmail} />
            <DetailField label={t('billing.eInvoiceAddress')} value={company.eInvoiceAddress} />
            <DetailField label={t('billing.ovtId')} value={company.ovtId} />
            <DetailField label={t('billing.eInvoiceOperator')} value={company.eInvoiceOperator ?? null} />
            <DetailField
              label={t('billing.paymentTermsDays')}
              value={company.paymentTermsDays != null ? String(company.paymentTermsDays) : null}
            />
            <DetailField
              label={t('billing.consolidatedInvoicing')}
              value={yesNo(consolidation.consolidatedInvoicing)}
            />
            {consolidation.consolidatedInvoicing && (
              <DetailField
                label={t('billing.consolidatedAutoSend')}
                value={yesNo(consolidation.consolidatedAutoSend)}
              />
            )}
          </Stack>
        </DetailSection>

        <PersonCompanyLinksSection companyId={company.id} />

        <PermissionGuard permission="vehicles.read">
          <PartyVehiclesSection companyId={company.id} />
        </PermissionGuard>

        <PermissionGuard permission="work_orders.read">
          <PartyWorkOrdersSection companyId={company.id} />
        </PermissionGuard>

        <CustomerEmailDeliverySection customerType="company" customerId={company.id} />

        <AttachmentsCard
          entityLinks={[{ entityType: 'company', entityId: company.id }]}
          canModify={() => true}
          defaultTags={[company.name]}
        />
      </Stack>

      <CompanyFormDialog open={editOpen} company={company} onClose={() => setEditOpen(false)} />
      <ConfirmDialog
        open={confirmDelete}
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage', { name: company.name })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
